import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { selectStyles } from '../components/selectTheme';
import { useSearchParams } from 'react-router-dom';
import {
  Receipt, CreditCard, MapPin, Wallet, LogOut, User,
  Plus, X, Package, BadgeCheck, ChevronDown, Gift, Star, ReceiptText,
} from 'lucide-react';
import ClubPanel from '../components/ClubPanel';
import Modal from '../components/Modal';
import AddressModal from '../components/AddressModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { downloadInvoicePdf } from '../utils/invoicePdf';
import CustomerAccount from '../components/CustomerAccount';
import { ManualPayBoxes } from '../components/ShopAccount';
import IdentityModal from '../components/IdentityModal';
import Pager, { paginate } from '../components/Pager';
import { useFeature } from '../tenant';
import { API_BASE } from '../config';
import { customerToken, clearCustomer } from '../sections/SiteChrome';

const inStyle = { width: '100%', padding: '11px 14px', borderRadius: 12, border: '1.5px solid var(--border)', fontFamily: 'inherit', background: 'var(--card)', color: 'var(--fg)', outline: 'none', marginBottom: 10, boxSizing: 'border-box', fontSize: '0.88rem' };
const btn = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 24px', borderRadius: 12, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontSize: '0.86rem', fontWeight: 700, cursor: 'pointer' };
const card = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 10 };
const secTitle = { margin: '0 0 10px', fontSize: '0.92rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7 };
const emptyBox = { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 32, textAlign: 'center', color: 'var(--muted-fg)', fontSize: '0.86rem', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' };

const TABS = [
  { key: 'orders', label: 'سفارش‌ها', Icon: Receipt },
  { key: 'wallet', label: 'کیف پول', Icon: Wallet },
  { key: 'club', label: 'باشگاه', Icon: Star },
  { key: 'profile', label: 'پروفایل', Icon: User },
];
const faStatus = { pending: 'در انتظار', paid: 'پرداخت‌شده', preparing: 'در حال آماده‌سازی', shipped: 'ارسال‌شده', completed: 'تکمیل‌شده', cancelled: 'لغوشده', failed: 'ناموفق' };
const statusColor = { pending: 'var(--accent)', paid: '#60A5FA', preparing: '#A78BFB', shipped: '#C084FC', completed: '#16A34A', cancelled: '#DC2626', failed: '#DC2626' };
const statusBg = { pending: 'var(--muted)', paid: 'var(--muted)', preparing: 'var(--muted)', shipped: 'var(--muted)', completed: 'var(--muted)', cancelled: 'var(--muted)', failed: 'var(--muted)' };
const payFa = { unpaid: 'پرداخت‌نشده', pending: 'در انتظار', paid: 'پرداخت‌شده', failed: 'ناموفق', refunded: 'برگشت‌خورده' };
const shipFa = { shipping: 'ارسال پستی', pickup: 'تحویل حضوری' };
const GW_FA = { zarinpal: 'زرین‌پال', zibal: 'زیبال', manual: 'واریز مستقیم', admin: 'ادمین', wallet: 'کیف پول' };
/* Direction of a wallet transaction: money in (واریز) vs money out (برداشت). */
const TX_IN = ['wallet_charge', 'manual', 'wallet_refund'];
const TX_OUT = ['wallet_pay', 'shipping_pay', 'online'];
const txDir = t => {
  if (TX_IN.includes(t.type)) return 'in';
  if (TX_OUT.includes(t.type)) return 'out';
  return Number(t.amount || 0) < 0 ? 'out' : 'in';
};
const txBadge = t => {
  const out = txDir(t) === 'out';
  return <span style={{ display: 'inline-block', fontSize: '0.66rem', fontWeight: 800, borderRadius: 20, padding: '3px 10px', background: 'var(--muted)', color: out ? '#DC2626' : '#16A34A' }}>{out ? '− برداشت' : '+ واریز'}</span>;
};
const txSigned = t => {
  const v = Number(t.amount || 0);
  const out = txDir(t) === 'out';
  return <span style={{ color: out ? '#DC2626' : '#16A34A' }}>{out ? '−' : '+'}{v.toLocaleString('fa-IR')}</span>;
};
const txTitle = t => {
  if (t.note) return t.note;
  if (t.type === 'wallet_charge') return t.gateway === 'manual' ? 'شارژ با فیش واریزی' : 'شارژ آنلاین کیف پول';
  if (t.type === 'wallet_pay') return `پرداخت سفارش #${t.order_id} از کیف پول`;
  if (t.type === 'shipping_pay') return `هزینه ارسال سفارش #${t.order_id}`;
  if (t.type === 'wallet_refund') return `برگشت وجه سفارش #${t.order_id}`;
  if (t.type === 'wallet_adjust') return 'تنظیم موجودی توسط ادمین';
  if (t.type === 'online') return `پرداخت آنلاین سفارش #${t.order_id}`;
  if (t.type === 'manual') return 'واریز مستقیم (در انتظار تایید)';
  return `سفارش #${t.order_id}`;
};

function authHeaders() {
  return { Authorization: `Bearer ${customerToken()}` };
}

export default function AccountPage() {
  const [loggedIn, setLoggedIn] = useState(!!customerToken());
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('orders');
  const toastOpts = { position: 'bottom-right', autoClose: 5000, theme: 'colored' };
  const [openOrder, setOpenOrder] = useState(null);
  const [orderPage, setOrderPage] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const [editingAddr, setEditingAddr] = useState(null);
  // charge modal
  const [chargeOpen, setChargeOpen] = useState(false);
  const [chargeTab, setChargeTab] = useState('online');
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeGateway, setChargeGateway] = useState('');
  const [gateways, setGateways] = useState([]);
  const [shopCards, setShopCards] = useState([]);
  const [shopCardIdx, setShopCardIdx] = useState(0);
  const [manualReceipt, setManualReceipt] = useState(null);
  const [manualPreview, setManualPreview] = useState(null);
  // identity gate (admin: site_settings.kyc.profile_required)
  const [kycRequired, setKycRequired] = useState(false);
  const [idModalOpen, setIdModalOpen] = useState(false);
  // profile
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [addr, setAddr] = useState({ province: '', city: '', address: '', postal_code: '', is_default: false });
  const [showAddrForm, setShowAddrForm] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardForm, setCardForm] = useState({ card_number: '', bank: '', holder: '' });
  const [busy, setBusy] = useState(false);
  const [nationalCode, setNationalCode] = useState('');
  const [by, setBy] = useState('1370');
  const [bm, setBm] = useState('5');
  const [bd, setBd] = useState('12');
  const [searchParams, setSearchParams] = useSearchParams();
  const clubOn = useFeature('club'); // site-builder plan gate
  const storageBase = API_BASE.replace(/\/api\/?$/, '');

  const pickManual = f => {
    if (manualPreview) URL.revokeObjectURL(manualPreview);
    setManualReceipt(f);
    setManualPreview(f ? URL.createObjectURL(f) : null);
  };

  const load = async () => {
    if (!customerToken()) { setLoggedIn(false); return; }
    try {
      const r = await axios.get('/account/summary', { headers: authHeaders() });
      setData(r.data);
      setLoggedIn(true);
    } catch { clearCustomer(); setLoggedIn(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const c = searchParams.get('charge');
    if (c === 'ok') { toast.success('کیف پول شما با موفقیت شارژ شد.', toastOpts); setTab('wallet'); setSearchParams({}); }
    else if (c === 'failed') { toast.error('شارژ کیف پول ناموفق بود.', toastOpts); setSearchParams({}); }
  }, []);
  useEffect(() => {
    axios.get('/regions/provinces').then(r => setProvinces(r.data || [])).catch(() => {});
    axios.get('/site-settings').then(r => {
      const p = r.data.payments || {};
      const g = (p.active && p.active.length ? p.active : [p.gateway || 'zarinpal']).filter(x => x === 'zarinpal' || x === 'zibal');
      setGateways(g);
      if (g.length) setChargeGateway(g[0]);
      setShopCards(p.shop_cards || []);
      setKycRequired(!!r.data.kyc?.profile_required);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!addr.province) { setCities([]); return; }
    axios.get('/regions/cities', { params: { province: addr.province } }).then(r => setCities(r.data || [])).catch(() => {});
  }, [addr.province]);

  const logout = async () => {
    try { await axios.post('/auth/logout', {}, { headers: authHeaders() }); } catch {}
    clearCustomer();
    window.location.reload();
  };

  const chargeWallet = async () => {
    
    const amount = Number(chargeAmount || 0);
    if (!amount || amount < 1000) { toast.warn('مبلغ شارژ حداقل ۱٬۰۰۰ تومان باشد.', toastOpts); return; }
    setBusy(true);
    try {
      const r = await axios.post('/wallet/charge', { amount, gateway: chargeGateway || undefined }, { headers: authHeaders() });
      window.location.href = r.data.url;
    } catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
    setBusy(false);
  };

  const submitManualCharge = async (skipIdentity = false) => {
    
    if (!skipIdentity && kycRequired && !data?.user?.national_code) { setIdModalOpen(true); return; }
    const amount = Number(chargeAmount || 0);
    if (!amount || amount < 1000) { toast.warn('مبلغ شارژ حداقل ۱٬۰۰۰ تومان باشد.', toastOpts); return; }
    if (!manualReceipt) { toast.warn('لطفاً تصویر فیش واریزی را انتخاب کنید.', toastOpts); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('amount', amount);
      fd.append('shop_card', shopCardIdx);
      fd.append('receipt', manualReceipt);
      const r = await axios.post('/wallet/manual', fd, { headers: authHeaders() });
      toast.success(r.data.message, toastOpts);
      pickManual(null);
      setChargeAmount('');
      setChargeOpen(false);
      setTab('wallet');
      load();
    } catch (e) {
      if (e.response?.data?.need_identity) { setIdModalOpen(true); }
      else toast.error((e.response?.data?.message || e.message), toastOpts);
    }
    setBusy(false);
  };

  const submitProfile = async () => {
    
    if (!/^\d{10}$/.test(nationalCode)) { toast.warn('کد ملی باید ۱۰ رقم باشد.', toastOpts); return; }
    const birth = `${by}/${String(bm).padStart(2, '0')}/${String(bd).padStart(2, '0')}`;
    setBusy(true);
    try {
      await axios.post('/auth/profile', { national_code: nationalCode, birth_date: birth }, { headers: authHeaders() });
      setNationalCode('');
      toast.success('مشخصات شما ثبت شد.', toastOpts);
      load();
    } catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
    setBusy(false);
  };

  const cancelAddrForm = () => {
    setAddr({ province: '', city: '', address: '', postal_code: '', is_default: false });
    setShowAddrForm(false);
    setEditingAddr(null);
  };

  const startEditAddr = a => {
    setEditingAddr(a.id);
    setAddr({ province: a.province || '', city: a.city || '', address: a.address || '', postal_code: a.postal_code || '', is_default: !!a.is_default });
    setShowAddrForm(true);
    
  };

  const saveAddress = async (f) => {
    
    const form = f || addr;
    if (!form.address) { toast.warn('متن آدرس را وارد کنید.', toastOpts); return; }
    setBusy(true);
    try {
      if (editingAddr) {
        await axios.put(`/account/addresses/${editingAddr}`, form, { headers: authHeaders() });
        toast.success('آدرس به‌روزرسانی شد.', toastOpts);
      } else {
        await axios.post('/account/addresses', { ...form, is_default: form.is_default || !(data?.addresses || []).length }, { headers: authHeaders() });
        toast.success('آدرس جدید ذخیره شد.', toastOpts);
      }
      cancelAddrForm();
      load();
    } catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
    setBusy(false);
  };

  const delAddress = async id => {
    try { await axios.delete(`/account/addresses/${id}`, { headers: authHeaders() }); load(); }
    catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
  };

  const addCard = async () => {
    
    setBusy(true);
    try {
      await axios.post('/account/cards', cardForm, { headers: authHeaders() });
      setCardForm({ card_number: '', bank: '', holder: '' });
      setShowCardForm(false);
      load();
    } catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
    setBusy(false);
  };

  const delCard = async id => {
    try { await axios.delete(`/account/cards/${id}`, { headers: authHeaders() }); load(); }
    catch (e) { toast.error((e.response?.data?.message || e.message), toastOpts); }
  };

  const downloadOrderPdf = o => {
    const names = Array.isArray(o.items) && o.items.length
      ? o.items.map(ln => `${ln.name || ''} × ${Number(ln.qty || 1).toLocaleString('fa-IR')}`).join('، ')
      : o.product?.name;
    downloadInvoicePdf({
      orderId: o.id, productName: names, productCategory: '',
      basePrice: o.price, total: o.payable ?? o.price,
      subtotal: o.price, discount: o.discount_amount, discountCode: o.discount_code,
      feePercent: o.fee_percent, fee: o.fee_amount,
      taxPercent: o.tax_percent, tax: o.tax_amount,
      shippingMethod: o.shipping_method_label || o.shipping_method, shippingCost: o.shipping_cost,
      userName: o.customer_name, userPhone: o.user_phone,
      userProvince: o.user_province, userCity: o.user_city,
      userAddress: o.user_address, userPostalCode: o.user_postal_code,
      gatewayLabel: '', paymentRef: o.shipping_tracking || '',
      date: o.created_at ? new Date(o.created_at).toLocaleDateString('fa-IR') : undefined,
    });
  };

  if (!loggedIn) {
    return (
      <div style={{ padding: '8px 16px 40px' }}>
        <CustomerAccount onDone={() => load()} />
      </div>
    );
  }

  const orders = data?.orders || [];
  const txs = data?.transactions || [];
  const addresses = data?.addresses || [];
  const cards = data?.cards || [];
  const walletBal = Number(data?.wallet?.balance ?? data?.user?.wallet_balance ?? 0);
  const verified = !!data?.user?.national_code;
  const orderSlice = paginate(orders, orderPage, 5);
  const txSlice = paginate(txs, txPage, 6);
  const visibleTabs = TABS.filter(t => t.key !== 'club' || clubOn !== false);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 16px 56px', direction: 'rtl' }}>
      {/* ── compact header: who + wallet ── */}
      <div style={{ background: 'linear-gradient(135deg, #18181B 0%, #3F3F46 100%)', borderRadius: 20, padding: 18, color: '#fff', boxShadow: '0 12px 32px rgba(24,24,27,0.28)', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <User size={24} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              {data?.user?.phone}
              {verified && <BadgeCheck size={16} color="#86EFAC" />}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.65)' }}>
              {verified ? 'احراز هویت شده ✓' : 'حساب کاربری شما'}
            </div>
          </div>
          <button onClick={logout} aria-label="خروج"
            style={{ width: 40, height: 40, borderRadius: 12, border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <LogOut size={17} color="#fff" />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, background: 'rgba(255,255,255,0.08)', borderRadius: 14, padding: '10px 14px' }}>
          <Wallet size={22} color="#FFD75E" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>کیف پول</div>
            <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{walletBal.toLocaleString('fa-IR')} <span style={{ fontSize: '0.7em', fontWeight: 600 }}>تومان</span></div>
          </div>
          <button onClick={() => { setChargeOpen(true);  }}
            style={{ padding: '9px 22px', borderRadius: 12, border: 'none', background: '#FFD75E', color: '#232326', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer' }}>
            + شارژ
          </button>
        </div>
      </div>

      {/* ── tabs ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setOrderPage(1); setTxPage(1); }}
            style={{
              flex: 1, padding: '11px 6px', borderRadius: 14, border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: tab === t.key ? 700 : 500,
              background: tab === t.key ? 'var(--primary)' : 'var(--card)', color: tab === t.key ? 'var(--on-primary)' : 'var(--muted-fg)',
              boxShadow: tab === t.key ? '0 4px 14px rgba(24,24,27,0.25)' : '0 1px 3px rgba(0,0,0,0.07)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap',
            }}>
            <t.Icon size={15} color={tab === t.key ? '#FFD75E' : '#A16207'} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── orders: compact + expandable ── */}
      {tab === 'orders' && (
        orders.length ? (<>
          {orderSlice.items.map(o => {
          const open = openOrder === o.id;
          const payRefs = [...(o.pay_refs || []), ...(o.payment_ref && !(o.pay_refs || []).includes(o.payment_ref) ? [o.payment_ref] : [])];
          const steps = [
            { label: 'ثبت', on: true },
            { label: 'پرداخت', on: o.payment_status === 'paid' || o.payment_status === 'refunded' },
            { label: o.shipping_method === 'pickup' ? 'آماده تحویل' : 'ارسال', on: ['shipping', 'ready_pickup', 'delivered'].includes(o.shipping_status) || ['shipped', 'completed'].includes(o.status) },
            { label: 'تحویل', on: o.shipping_status === 'delivered' || o.status === 'completed' },
          ];
          return (
            <div key={o.id} style={card}>
              <button onClick={() => setOpenOrder(open ? null : o.id)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'right', padding: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Package size={20} color="#52525B" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>سفارش #{o.id}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)' }}>
                    {o.created_at ? new Date(o.created_at).toLocaleDateString('fa-IR') : ''} · {Number(o.payable ?? o.price ?? 0).toLocaleString('fa-IR')} تومان
                  </div>
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, background: statusBg[o.status] || 'var(--muted)', color: statusColor[o.status] || 'var(--muted-fg)', borderRadius: 20, padding: '5px 12px', whiteSpace: 'nowrap' }}>
                  {faStatus[o.status] || o.status}
                </span>
                <ChevronDown size={17} color="#A1A1AA" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
              </button>
              {open && (
                <div style={{ marginTop: 12, borderTop: '1px solid #F4F4F5', paddingTop: 10, fontSize: '0.82rem' }}>
                  {(Array.isArray(o.items) && o.items.length ? o.items : (o.product ? [{ name: o.product.name, qty: o.qty || 1 }] : [])).map((ln, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: 'var(--muted-fg)' }}>
                      <span>{ln.name || '—'} <span style={{ color: 'var(--muted-fg)' }}>× {Number(ln.qty || 1).toLocaleString('fa-IR')}</span></span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}><span style={{ color: 'var(--muted-fg)' }}>جمع</span><b>{Number(o.price || 0).toLocaleString('fa-IR')} تومان</b></div>
                  {!!o.discount_amount && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', color: '#16A34A' }}><span>تخفیف{o.discount_code ? ` (${o.discount_code})` : ''}</span><b>−{Number(o.discount_amount).toLocaleString('fa-IR')}</b></div>}
                  {!!o.shipping_cost && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}><span style={{ color: 'var(--muted-fg)' }}>ارسال</span><b>{Number(o.shipping_cost).toLocaleString('fa-IR')}</b></div>}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: 4, borderTop: '1px solid #F4F4F5', fontWeight: 800 }}>
                    <span>قابل پرداخت</span><span>{Number(o.payable ?? o.price ?? 0).toLocaleString('fa-IR')} تومان</span>
                  </div>
                  <div style={{ marginTop: 8, background: 'var(--muted)', borderRadius: 10, padding: '8px 12px', fontSize: '0.76rem', color: 'var(--muted-fg)' }}>
                    {shipFa[o.shipping_method] || o.shipping_method || '—'}
                    {o.shipping_tracking ? <> · کد پیگیری: <b dir="ltr">{o.shipping_tracking}</b></> : null}
                    {payRefs.length ? <> · رسید پرداخت: <b dir="ltr">{payRefs.join(' ، ')}</b></> : null}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginTop: 12 }}>
                    {steps.map((s, i) => (
                      <React.Fragment key={i}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 52 }}>
                          <span style={{ width: 22, height: 22, borderRadius: '50%', background: s.on ? 'var(--primary)' : 'var(--muted)', color: s.on ? 'var(--on-primary)' : 'var(--muted-fg)', fontSize: '0.7rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.on ? '✓' : (i + 1).toLocaleString('fa-IR')}</span>
                          <span style={{ fontSize: '0.64rem', color: s.on ? 'var(--fg)' : 'var(--muted-fg)', fontWeight: s.on ? 700 : 400 }}>{s.label}</span>
                        </div>
                        {i < steps.length - 1 && <div style={{ flex: 1, height: 2, background: steps[i + 1].on ? 'var(--primary)' : 'var(--border)', margin: '0 2px 20px', borderRadius: 2 }} />}
                      </React.Fragment>
                    ))}
                  </div>
                  <button onClick={() => downloadOrderPdf(o)}
                    style={{ width: '100%', marginTop: 10, padding: '10px', borderRadius: 10, border: '1.5px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                    دانلود فاکتور
                  </button>
                </div>
              )}
            </div>
          );
        })}
          <Pager page={orderSlice.page} pages={orderSlice.pages} onChange={setOrderPage} />
        </>) : (
          <div style={emptyBox}>
            <Receipt size={32} color="#D4D4D8" style={{ margin: '0 auto 8px' }} />
            <div>هنوز سفارشی ثبت نکرده‌اید.</div>
          </div>
        )
      )}

      {/* ── wallet: transactions ── */}
      {tab === 'wallet' && (
        txs.length ? (<>
          {txSlice.items.map(t => (
          <div key={t.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Wallet size={19} color={txDir(t) === 'out' ? '#DC2626' : '#16A34A'} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '0.86rem' }}>{txTitle(t)}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)' }}>
                  {GW_FA[t.gateway] || t.gateway}{t.ref_number ? ` · رسید ${t.ref_number}` : ''}{t.receipt_path ? <> · <a href={`${storageBase}/storage/${t.receipt_path}`} target="_blank" rel="noreferrer" style={{ color: '#A16207', fontWeight: 700 }}>مشاهده فیش</a></> : ''}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'left', flexShrink: 0 }}>
              <div style={{ marginBottom: 3 }}>{txBadge(t)}</div>
              <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{txSigned(t)}</div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: t.status === 'paid' ? '#16A34A' : t.status === 'pending' ? 'var(--accent)' : '#DC2626' }}>
                {t.status === 'paid' ? 'موفق' : t.status === 'pending' ? 'در انتظار تایید' : 'ناموفق'}
              </div>
            </div>
          </div>
          ))}
          <Pager page={txSlice.page} pages={txSlice.pages} onChange={setTxPage} />
        </>) : (
          <div style={emptyBox}>
            <Wallet size={32} color="#D4D4D8" style={{ margin: '0 auto 8px' }} />
            <div>تراکنشی وجود ندارد.</div>
          </div>
        )
      )}

      {tab === 'club' && clubOn !== false && <ClubPanel />}

      {/* ── profile: identity + addresses + cards + referral ── */}
      {tab === 'profile' && (<>
        {!verified && (
          <div style={{ background: 'var(--muted)', border: '1.5px solid #FDE68A', borderRadius: 16, padding: 16, marginBottom: 10 }}>
            <div style={{ fontWeight: 800, marginBottom: 4, color: 'var(--accent)', fontSize: '0.88rem' }}>تکمیل مشخصات (احراز هویت)</div>
            <p style={{ margin: '0 0 10px', fontSize: '0.78rem', color: 'var(--accent)' }}>کد ملی و تاریخ تولد شمسی را وارد کنید.</p>
            <input value={nationalCode} onChange={e => setNationalCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))} placeholder="کد ملی ۱۰ رقمی" style={{ ...inStyle, textAlign: 'center' }} dir="ltr" inputMode="numeric" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
              <select value={bd} onChange={e => setBd(e.target.value)} style={{ ...inStyle, marginBottom: 0 }}>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={bm} onChange={e => setBm(e.target.value)} style={{ ...inStyle, marginBottom: 0 }}>
                {['فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور', 'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'].map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
              </select>
              <select value={by} onChange={e => setBy(e.target.value)} style={{ ...inStyle, marginBottom: 0 }}>
                {Array.from({ length: 106 }, (_, i) => 1405 - i).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button onClick={submitProfile} disabled={busy} style={{ ...btn, width: '100%', opacity: busy ? 0.55 : 1 }}>ثبت مشخصات</button>
          </div>
        )}

        {data?.user?.referral_code && (
          <div style={{ background: 'var(--muted)', border: '1px solid #FDE68A', borderRadius: 16, padding: 14, marginBottom: 10, fontSize: '0.82rem' }}>
            <div style={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6 }}><Gift size={16} color="#B45309" /> دعوت دوستان: <b style={{ letterSpacing: 1 }}>{data.user.referral_code}</b></div>
            <div style={{ color: 'var(--muted-fg)', marginTop: 4 }}>
              {Number(data.user.referrals_count || 0).toLocaleString('fa-IR')} نفر عضو شده‌اند
              {Number(data.user.referral_purchases || 0) > 0 && <> · مجموع خریدشان: <b>{Number(data.user.referral_purchases).toLocaleString('fa-IR')} تومان</b></>}
            </div>
          </div>
        )}

        <div style={card}>
          <h3 style={secTitle}><MapPin size={16} color="#A16207" /> آدرس‌ها</h3>
          {addresses.map(a => (
            <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, padding: '10px 0', borderTop: '1px solid #F4F4F5', fontSize: '0.84rem' }}>
              <div style={{ lineHeight: 1.9, minWidth: 0 }}>
                <b>{[a.province, a.city].filter(Boolean).join('، ')}</b>
                <br /><span style={{ color: 'var(--muted-fg)' }}>{a.address}</span>
                {a.postal_code && <><br /><span style={{ color: 'var(--muted-fg)', fontSize: '0.74rem' }}>کدپستی: {a.postal_code}</span></>}
                {a.is_default && <span style={{ fontSize: '0.68rem', fontWeight: 700, background: 'var(--muted)', color: '#16A34A', borderRadius: 20, padding: '3px 10px', marginRight: 6 }}>پیش‌فرض</span>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => startEditAddr(a)} aria-label="ویرایش آدرس"
                  style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--card)', color: 'var(--fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.95rem' }}>
                  ✎
                </button>
                <button onClick={() => delAddress(a.id)} aria-label="حذف آدرس"
                  style={{ width: 32, height: 32, borderRadius: 10, border: '1px solid #FECACA', background: 'var(--card)', color: 'var(--fg)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <X size={14} color="#DC2626" />
                </button>
              </div>
            </div>
          ))}
          {
            <button onClick={() => { setEditingAddr(null); setAddr({ province: '', city: '', address: '', postal_code: '', is_default: addresses.length === 0 }); setShowAddrForm(true); }}
              style={{ width: '100%', padding: 13, borderRadius: 12, border: '1.5px dashed var(--border)', background: 'var(--card)', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--fg)', fontSize: '0.84rem', fontWeight: 700, marginTop: 6 }}>
              <Plus size={16} color="#A16207" /> افزودن آدرس جدید
            </button>
          }
          {showAddrForm && (
            <AddressModal
              open={showAddrForm}
              onClose={cancelAddrForm}
              title={editingAddr ? 'ویرایش آدرس' : 'آدرس جدید'}
              initial={addr}
              submitLabel={editingAddr ? 'ذخیره تغییرات' : 'ذخیره آدرس'}
              busy={busy}
              onSubmit={f => saveAddress(f)}
            />
          )}
        </div>

        <div style={card}>
          <h3 style={secTitle}><CreditCard size={16} color="#A16207" /> کارت‌های بانکی</h3>
          {cards.map(c => (
            <div key={c.id} style={{ background: 'linear-gradient(135deg, #A16207 0%, #713F12 100%)', borderRadius: 14, padding: 14, color: '#fff', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, letterSpacing: 2, fontSize: '0.95rem' }} dir="ltr">{c.masked}</div>
                <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.8)' }}>{[c.bank, c.holder].filter(Boolean).join(' · ') || 'کارت بانکی'}</div>
              </div>
              <button onClick={() => delCard(c.id)} aria-label="حذف کارت"
                style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(0,0,0,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <X size={14} color="#fff" />
              </button>
            </div>
          ))}
          {
<button onClick={() => setShowCardForm(true)}
              style={{ width: '100%', padding: 13, borderRadius: 12, border: '1.5px dashed var(--border)', background: 'var(--card)', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--fg)', fontSize: '0.84rem', fontWeight: 700, marginTop: 6 }}>
              <Plus size={16} color="#A16207" /> افزودن کارت بانکی
            </button>
}
{showCardForm && (
<Modal open={showCardForm} onClose={() => setShowCardForm(false)} title="افزودن کارت بانکی">

            <div>
              <input value={cardForm.card_number} onChange={e => setCardForm({ ...cardForm, card_number: e.target.value.replace(/[^0-9]/g, '').slice(0, 16) })} placeholder="شماره ۱۶ رقمی کارت" style={{ ...inStyle, textAlign: 'center' }} dir="ltr" inputMode="numeric" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <input value={cardForm.bank} onChange={e => setCardForm({ ...cardForm, bank: e.target.value })} placeholder="بانک" style={inStyle} />
                <input value={cardForm.holder} onChange={e => setCardForm({ ...cardForm, holder: e.target.value })} placeholder="نام صاحب کارت" style={inStyle} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={addCard} disabled={busy} style={{ ...btn, flex: 1, opacity: busy ? 0.55 : 1 }}>ذخیره کارت</button>
                <button onClick={() => setShowCardForm(false)} style={{ ...btn, flex: 1, background: 'var(--muted)', color: 'var(--fg)' }}>انصراف</button>
              </div>
            </div>
</Modal>
)}
        </div>
      </>)}

      {/* ── charge modal: one place for both methods ── */}
      <Modal open={chargeOpen} onClose={() => setChargeOpen(false)} title="شارژ کیف پول" contentHeight="min(620px, 90vh)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--muted)', border: '1px solid var(--border)', borderRadius: 'var(--radius, 12px)', padding: '12px 16px', marginBottom: 14 }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: 'var(--primary)', color: 'var(--on-primary)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Wallet size={20} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: '0.74rem', color: 'var(--muted-fg)' }}>موجودی فعلی</span>
            <strong style={{ display: 'block', fontSize: '1.02rem', color: 'var(--fg)' }}>{Number(walletBal ?? 0).toLocaleString('fa-IR')} تومان</strong>
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, background: 'var(--muted)', borderRadius: 14, padding: 5 }}>
          <button onClick={() => setChargeTab('online')}
            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px', borderRadius: 10, border: 'none', background: chargeTab === 'online' ? 'var(--card)' : 'transparent', color: chargeTab === 'online' ? 'var(--fg)' : 'var(--muted-fg)', boxShadow: chargeTab === 'online' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer' }}>
            <CreditCard size={15} /> پرداخت آنلاین
          </button>
          {shopCards.length > 0 && (
            <button onClick={() => setChargeTab('manual')}
              style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px', borderRadius: 10, border: 'none', background: chargeTab === 'manual' ? 'var(--card)' : 'transparent', color: chargeTab === 'manual' ? 'var(--fg)' : 'var(--muted-fg)', boxShadow: chargeTab === 'manual' ? '0 1px 4px rgba(0,0,0,0.1)' : 'none', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 800, cursor: 'pointer' }}>
              <ReceiptText size={15} /> واریز + فیش
            </button>
          )}
        </div>
        <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>مبلغ شارژ (تومان)</label>
        <input value={chargeAmount} onChange={e => setChargeAmount(e.target.value.replace(/[^0-9]/g, ''))} placeholder="مثلاً ۱٬۰۰۰٬۰۰۰" inputMode="numeric"
          style={{ ...inStyle, textAlign: 'center', marginBottom: 8, background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--fg)', fontWeight: 800, fontSize: '1rem' }} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          {[50000, 100000, 200000, 500000].map(v => (
            <button key={v} onClick={() => setChargeAmount(String(v))}
              style={{ flex: '1 1 0', padding: '8px 4px', borderRadius: 10, border: String(v) === String(chargeAmount) ? '2px solid var(--primary)' : '1px solid var(--border)', background: String(v) === String(chargeAmount) ? 'var(--muted)' : 'var(--card)', color: 'var(--fg)', fontFamily: 'inherit', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {v.toLocaleString('fa-IR')}
            </button>
          ))}
        </div>
        {!!chargeAmount && <div style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--accent)', fontWeight: 800, marginBottom: 12 }}>{Number(chargeAmount).toLocaleString('fa-IR')} تومان</div>}
        {chargeTab === 'online' ? (<>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>درگاه پرداخت را انتخاب کنید</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
            {gateways.map(g => {
              const active = chargeGateway === g;
              return (
                <div key={g} onClick={() => setChargeGateway(g)} role="radio" aria-checked={active} tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setChargeGateway(g); } }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, border: active ? '2px solid var(--primary)' : '1px solid var(--border)', background: active ? 'var(--muted)' : 'var(--card)', borderRadius: 13, padding: '11px 14px', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' }}>
                  <span style={{ width: 38, height: 38, borderRadius: 11, background: active ? 'var(--primary)' : 'var(--muted)', color: active ? 'var(--on-primary)' : 'var(--fg)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <CreditCard size={18} />
                  </span>
                  <strong style={{ flex: 1, fontSize: '0.86rem', color: 'var(--fg)' }}>{g === 'zarinpal' ? 'زرین‌پال' : 'زیبال'}</strong>
                  <span style={{ fontSize: '0.7rem', color: 'var(--muted-fg)' }}>پرداخت آنلاین</span>
                  <span style={{ width: 22, height: 22, borderRadius: '50%', border: active ? '6px solid var(--primary)' : '2px solid var(--border)', background: 'var(--card)', flexShrink: 0, boxSizing: 'border-box' }} />
                </div>
              );
            })}
          </div>
          <button onClick={chargeWallet} disabled={busy || !gateways.length} style={{ ...btn, width: '100%', background: 'var(--primary)', color: 'var(--on-primary)', opacity: busy || !gateways.length ? 0.6 : 1 }}>
            <CreditCard size={16} /> {busy ? 'در حال اتصال...' : 'پرداخت و شارژ'}
          </button>
        </>) : (<>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--fg)', marginBottom: 8 }}>کارت فروشگاه را انتخاب کنید و مبلغ را واریز کنید</label>
          <div style={{ marginBottom: 12 }}>
            <ManualPayBoxes cards={shopCards} selected={shopCardIdx} onSelect={setShopCardIdx} />
          </div>
          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--fg)', marginBottom: 6 }}>تصویر فیش واریزی</label>
          <input type="file" accept="image/*" onChange={e => pickManual(e.target.files[0] || null)}
            style={{ ...inStyle, background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--fg)' }} />
          {manualPreview && (
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <img src={manualPreview} alt="پیش‌نمایش فیش" style={{ width: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--muted)' }} />
              <button onClick={() => pickManual(null)} aria-label="حذف فیش"
                style={{ position: 'absolute', top: 8, left: 8, width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
            </div>
          )}
          <button onClick={submitManualCharge} disabled={busy} style={{ ...btn, width: '100%', background: 'var(--primary)', color: 'var(--on-primary)', opacity: busy ? 0.6 : 1 }}>
            <ReceiptText size={16} /> {busy ? 'در حال ارسال...' : 'ثبت فیش (شارژ پس از تایید)'}
          </button>
        </>)}
      </Modal>
      <IdentityModal open={idModalOpen} onClose={() => setIdModalOpen(false)}
        note="برای ثبت فیش واریزی، ابتدا احراز هویت (کد ملی و تاریخ تولد) را تکمیل کنید."
        onVerified={u => { setData(d => (d ? { ...d, user: { ...(d.user || {}), ...(u || {}) } } : d)); setIdModalOpen(false); submitManualCharge(true); }} />
      <ToastContainer position="bottom-right" autoClose={5000} rtl theme="colored" style={{ zIndex: 99999 }} />
    </div>
  );
}